import React, { type PropsWithChildren } from 'react';
import { lazy, LazySuspense } from 'react-loosely-lazy';
import { IntlProvider, type IntlShape, type MessageDescriptor } from 'react-intl-next';

import ReactDOM from 'react-dom';

import type { InlineCardDefinition } from '@atlaskit/adf-schema';
import { AnalyticsContext } from '@atlaskit/analytics-next';

import type { CreatePayload } from '@atlaskit/link-create';
import type { CreatePluginPresetsConfig } from '@atlassian/link-create-presets';
import type { AnalyticsReactContextInterface } from '@atlaskit/analytics-next-stable-react-context';
import type { ExtensionManifest } from '@atlaskit/editor-common/extensions';
import { browser } from '@atlaskit/editor-common/utils';

import { LinkCreateModalProvider } from './link-create-modal-provider';
import { keywords, jiraExtensionMessages, confluenceExtensionMessages } from './ui/messages';
import type { ManifestProps } from './types';
import type { CreateFlags } from './common/types';
import { ErrorBoundary } from './ui/error-boundary';
import {
	INLINE_CREATE_ONE_STEP_COHORTS,
	getInlineCreateOneStepExperimentEnabled,
} from './inline-create-one-step-experiment';

// as const so that values can be looked up for i18n in ./ui/messages/keywords
const JIRA_KEYWORDS = ['create', 'issue', 'ticket', 'raise', 'new'] as const;
const CONFLUENCE_KEYWORDS = ['create', 'page', 'new', 'space', 'publish'] as const;

// categories will put us into tabs in the quick insert browser (the plus menu)
const JIRA_CATEGORIES = ['development', 'external-content'];
const CONFLUENCE_CATEGORIES = ['confluence-content'];

export const manifest = ({
	intl,
	intlLocale,
	defaultCloudId,
	analyticsContext,
	location,
	gatewayBaseUrl,
	flags,
	defaultFormValues,
	pageContext,
	allowConfluenceEditViewEmbed,
}: ManifestProps): ExtensionManifest => {
	const formatMessage = (
		descriptor: Pick<MessageDescriptor, 'id' | 'description'> & {
			defaultMessage: string;
		},
	): string => {
		return intl?.formatMessage(descriptor) ?? descriptor.defaultMessage;
	};

	return {
		title: 'Link create editor extension',
		type: 'extension',
		key: 'com.atlassian.linking-platform.create',
		description:
			'Configures quick insert commands for creating objects, like Jira issues and Confluence pages as links',
		icons: {
			'48': () =>
				import(
					/* webpackChunkName: "@atlaskit-internal_@atlassian/editor-extension-link-create-icon" */ './common/ui/icons/plussquare'
				).then((mod) => mod.default),
		},
		modules: {
			quickInsert: [
				{
					key: 'linking-platform-create-jira-issue',
					title: formatMessage(jiraExtensionMessages.extensionTitle),
					description: formatMessage(jiraExtensionMessages.extensionDescription),
					featured: true,
					categories: JIRA_CATEGORIES,
					keywords: ['jira', ...JIRA_KEYWORDS.map((keyword) => formatMessage(keywords[keyword]))],
					icon: () =>
						import(
							/* webpackChunkName: "@atlaskit-internal_@atlassian/editor-extension-link-create-icon" */ './common/ui/icons/plussquare'
						).then((mod) => mod.default),
					action: async () =>
						showCreateModal({
							intl,
							intlLocale,
							defaultCloudId,
							analyticsContext,
							entityKey: 'jira-issue',
							gatewayBaseUrl,
							location,
							flags,
							defaultFormValues,
						}),
				},
				{
					key: 'linking-platform-create-confluence-page',
					title: formatMessage(confluenceExtensionMessages.extensionTitle),
					description: formatMessage(confluenceExtensionMessages.extensionDescription),
					featured: true,
					categories: CONFLUENCE_CATEGORIES,
					keywords: [
						'confluence',
						...CONFLUENCE_KEYWORDS.map((keyword) => formatMessage(keywords[keyword])),
					],
					icon: () =>
						import(
							/* webpackChunkName: "@atlaskit-internal_@atlassian/editor-extension-link-create-icon-page" */ '@atlaskit/icon/core/migration/page'
						).then((mod) => mod.default),
					action: async () => {
						// Only do one step page creation experiment if we are
						// 1. in the full page editor of a confluence page
						// 2. it is not live edit or a blogpost
						// 3. user is not using Safari
						const shouldRunOneStepCreateExperiment =
							location === 'confluence-page' &&
							pageContext?.isFullPageEditor &&
							!pageContext?.isLivePage &&
							!pageContext?.isBlogpost &&
							!browser.safari;

						if (shouldRunOneStepCreateExperiment) {
							const { isInlineCreateOneStepExperimentEnabled, cohort } =
								getInlineCreateOneStepExperimentEnabled({
									shouldFireExperimentExposure: true,
								});
							if (isInlineCreateOneStepExperimentEnabled) {
								return oneStepCreate({
									intl,
									intlLocale,
									defaultCloudId,
									analyticsContext,
									gatewayBaseUrl,
									flags,
									defaultFormValues,
									cohort,
								});
							}
						}

						return showCreateModal({
							intl,
							intlLocale,
							defaultCloudId,
							analyticsContext,
							entityKey: 'confluence-page',
							gatewayBaseUrl,
							location,
							flags,
							defaultFormValues,
							allowConfluenceEditViewEmbed,
						});
					},
				},
			],
		},
	};
};

const DEFAULT_LOCALE = 'en';
const LINK_CREATE_REFERENCE_MOUNT_POINT_ID = 'link-create-mount-point';

const getMountPoint = (): HTMLElement => {
	let modalEl = document.getElementById(`${LINK_CREATE_REFERENCE_MOUNT_POINT_ID}`);

	if (modalEl === null) {
		modalEl = document.createElement('div');
		modalEl.id = LINK_CREATE_REFERENCE_MOUNT_POINT_ID;
		document.body.appendChild(modalEl);
	}

	return modalEl;
};

const AnalyticsProviderWrapper = ({
	children,
	value,
}: PropsWithChildren<{ value?: AnalyticsReactContextInterface }>) => {
	if (value === undefined) {
		return <>{children}</>;
	}
	return <LinkCreateModalProvider value={value}>{children}</LinkCreateModalProvider>;
};

const LazyEditorExtensionLinkCreate = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian_editor-extension-link-create-component" */
		'./ui/index'
	).then(({ EditorExtensionLinkCreate }) => ({
		default: EditorExtensionLinkCreate,
	})),
);

const LazyOneStepLinkCreate = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian_one-step-link-create" */
		'./inline-create-one-step-experiment/index'
	).then(({ OneStepLinkCreate }) => ({
		default: OneStepLinkCreate,
	})),
);

/*
 * Rather than rendering a modal and requesting user input, this function
 * creates a new page and immediately inserts it without any user input.
 * The title is generated on the backend so the new page is just created
 * and inserted as a smartlink. This is part of the Confluence Inline Create
 * One Step experiment.
 */
const oneStepCreate = ({
	intl,
	intlLocale,
	defaultCloudId,
	gatewayBaseUrl,
	analyticsContext,
	flags,
	defaultFormValues,
	cohort,
}: {
	intl?: IntlShape;
	intlLocale?: string;
	defaultCloudId: string;
	gatewayBaseUrl?: string;
	analyticsContext?: AnalyticsReactContextInterface;
	flags?: CreateFlags;
	defaultFormValues?: CreatePluginPresetsConfig['defaultFormValues'];
	cohort?: string;
}): Promise<InlineCardDefinition | void> => {
	return new Promise<InlineCardDefinition | void>(async (res) => {
		const mountPoint = getMountPoint();

		const handleCreate = (payload: CreatePayload) => {
			res({
				type: 'inlineCard',
				attrs: { url: payload.url },
			});

			// For experiment variant 2, we want to open the draft in a new tab and show the success flag
			if (cohort === INLINE_CREATE_ONE_STEP_COHORTS.VARIANT2) {
				const parentId = defaultFormValues?.['confluence-page']?.parentId ?? '';
				window.name = parentId;

				// createdFrom param indicates to the new draft window to show a success flag on load
				const url = new URL(payload.url);
				url.searchParams.set('createdFrom', parentId);

				window.open(url, '_blank');
			}
		};

		const handleComplete = () => {
			ReactDOM.unmountComponentAtNode(mountPoint);
		};

		ReactDOM.render(
			<AnalyticsProviderWrapper value={analyticsContext}>
				<IntlProvider
					messages={intl?.messages ?? {}}
					locale={intl?.locale ?? intlLocale ?? 'en'}
					defaultLocale={DEFAULT_LOCALE}
					onError={intl?.onError}
				>
					<AnalyticsContext
						data={{
							source: 'inlineCreateOneStep',
							attributes: {
								component: 'editorExtensionLinkCreate',
								location: 'confluence-page',
								triggeredFrom: 'quick-insert',
								experiment: 'ConfluenceInlineCreateOneStepExperiment',
							},
						}}
					>
						<ErrorBoundary
							flags={flags}
							onError={() => {
								/**
								 * If something goes wrong, resolve the promise to editor,
								 * and unmount the component mountpoint
								 */
								res();
								handleComplete();
							}}
						>
							<LazySuspense fallback={null}>
								<LazyOneStepLinkCreate
									onCreate={handleCreate}
									gatewayBaseUrl={gatewayBaseUrl}
									defaultCloudId={defaultCloudId}
									defaultFormValues={defaultFormValues}
								/>
							</LazySuspense>
						</ErrorBoundary>
					</AnalyticsContext>
				</IntlProvider>
			</AnalyticsProviderWrapper>,
			mountPoint,
		);
	});
};

const showCreateModal = ({
	intl,
	intlLocale,
	defaultCloudId,
	entityKey,
	gatewayBaseUrl,
	location,
	analyticsContext,
	flags,
	defaultFormValues,
	allowConfluenceEditViewEmbed,
}: {
	intl?: IntlShape;
	intlLocale?: string;
	defaultCloudId: string;
	entityKey: string;
	gatewayBaseUrl?: string;
	location?: string;
	analyticsContext?: AnalyticsReactContextInterface;
	flags?: CreateFlags;
	defaultFormValues?: CreatePluginPresetsConfig['defaultFormValues'];
	allowConfluenceEditViewEmbed?: boolean;
}): Promise<InlineCardDefinition | void> =>
	new Promise(async (res) => {
		const mountPoint = getMountPoint();

		const handleCreate = (payload: CreatePayload) => {
			res({
				type: 'inlineCard',
				attrs: { url: payload.url },
			});
		};

		const handleCancel = () => {
			res();
		};

		const handleCloseComplete = () => {
			ReactDOM.unmountComponentAtNode(mountPoint);
		};

		ReactDOM.render(
			<AnalyticsProviderWrapper value={analyticsContext}>
				<IntlProvider
					messages={intl?.messages ?? {}}
					locale={intl?.locale ?? intlLocale ?? 'en'}
					defaultLocale={DEFAULT_LOCALE}
					onError={intl?.onError}
				>
					<AnalyticsContext
						data={{
							attributes: {
								component: 'editorExtensionLinkCreate',
								location,
								triggeredFrom: 'quick-insert',
							},
						}}
					>
						<ErrorBoundary
							flags={flags}
							onError={() => {
								/**
								 * If something goes wrong, resolve the promise to editor,
								 * and unmount the component mountpoint
								 */
								res();
								handleCloseComplete();
							}}
						>
							<LazySuspense fallback={null}>
								<LazyEditorExtensionLinkCreate
									defaultCloudId={defaultCloudId}
									gatewayBaseUrl={gatewayBaseUrl}
									defaultFormValues={defaultFormValues}
									entityKey={entityKey}
									onCreate={handleCreate}
									onCancel={handleCancel}
									onCloseComplete={handleCloseComplete}
									flags={flags}
									allowConfluenceEditViewEmbed={allowConfluenceEditViewEmbed}
								/>
							</LazySuspense>
						</ErrorBoundary>
					</AnalyticsContext>
				</IntlProvider>
			</AnalyticsProviderWrapper>,
			mountPoint,
		);
	});
